import { type ReactNode } from 'react'
import { PermissionGate } from '@/components/auth/PermissionGate'
import { useIsMobile } from '@/hooks/use-mobile'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface EntityPageLayoutProps {
  title: string
  description?: string
  permission?: string
  testIdPrefix: string
  children: ReactNode
  isLoading?: boolean
  error?: Error | null
  headerActions?: ReactNode
  className?: string
}

export function EntityPageLayout({
  title,
  description,
  permission,
  testIdPrefix,
  children,
  isLoading = false,
  error = null,
  headerActions,
  className
}: EntityPageLayoutProps) {
  const isMobile = useIsMobile()

  const pageContent = (
    <div 
      data-testid={`${testIdPrefix}-page`} 
      className={cn("space-y-4 lg:space-y-6", className)}
    >
      {/* Header Section */}
      <div 
        className="space-y-2" 
        data-testid={`${testIdPrefix}-page-header`}
      >
        <div className={cn(
          "flex items-start justify-between gap-4",
          isMobile && "flex-col"
        )}>
          <div className="flex-1 space-y-1">
            <h1 
              className="text-2xl font-semibold tracking-tight lg:text-3xl"
              data-testid={`${testIdPrefix}-page-title`}
            >
              {title}
            </h1>
            {description && (
              <p 
                className="text-muted-foreground"
                data-testid={`${testIdPrefix}-page-description`}
              >
                {description}
              </p>
            )}
          </div>
          {headerActions && (
            <div 
              className={cn(
                "flex items-center gap-2",
                isMobile && "w-full justify-stretch"
              )}
              data-testid={`${testIdPrefix}-page-actions`}
            >
              {headerActions}
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div 
        data-testid={`${testIdPrefix}-content`}
        className="relative min-h-[200px]"
      >
        {isLoading ? (
          <div 
            className="space-y-4"
            data-testid={`${testIdPrefix}-loading`}
            role="status"
            aria-label="Loading content"
          >
            <Skeleton className="h-10 w-full max-w-md" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            {isMobile && <Skeleton className="h-20 w-full" />}
          </div>
        ) : error ? (
          <div 
            className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 lg:p-6"
            data-testid={`${testIdPrefix}-error`}
            role="alert"
          >
            <h3 className="text-lg font-medium text-destructive">
              Error loading {title.toLowerCase()}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {error.message || 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  )

  // Use PermissionGate for permission-based rendering
  if (permission) {
    return (
      <PermissionGate permission={permission}>
        {pageContent}
      </PermissionGate>
    )
  }

  return pageContent
}